import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WS_BASE_URL, QUERY_KEYS } from '../config/api';
import { WebSocketMessage, NewsUpdateMessage, MarketRegimeUpdateMessage, MonitoringUpdateMessage, TradeUpdateMessage, BotMetricsUpdateMessage, SpreadUpdateMessage, MomentumUpdateMessage, VelocityUpdateMessage, FragilityUpdateMessage, LogsUpdateMessage } from '../types';
import { InfrastructureMetrics, InfrastructureUpdateMessage } from '../types/infrastructure';

interface WebSocketContextType {
  connected: boolean;
  lastMessage: WebSocketMessage | null;
  error: string | null;
  infrastructureServers: Map<string, InfrastructureMetrics>;
  subscribeToNews: () => void;
  subscribeToMarketRegime: () => void;
  subscribeToMonitoring: () => void;
  subscribeToTrades: () => void;
  subscribeToBotMetrics: () => void;
  subscribeToInfrastructure: () => void;
  subscribeToSpread: () => void;
  subscribeToMomentum: () => void;
  subscribeToVelocity: () => void;
  subscribeToFragility: () => void;
  subscribeToLogs: () => void;
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [infrastructureServers, setInfrastructureServers] = useState<Map<string, InfrastructureMetrics>>(new Map());
  const queryClient = useQueryClient();

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connect = () => {
    try {
      // Create native WebSocket connection
      const socket = new WebSocket(`${WS_BASE_URL}/ws`);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('WebSocket connected');
        setConnected(true);
        setError(null);
      };

      socket.onclose = () => {
        console.log('WebSocket disconnected');
        setConnected(false);
        // Auto-reconnect after 2 seconds
        reconnectTimeoutRef.current = setTimeout(() => connect(), 2000);
      };

      socket.onerror = (err) => {
        console.error('WebSocket error:', err);
        setError('WebSocket connection failed');
        setConnected(false);
      };

      socket.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          setLastMessage(message);

          // Handle specific message types
          switch (message.type) {
            case 'news_update':
              handleNewsUpdate(message as NewsUpdateMessage);
              break;
            case 'market_regime_update':
              handleMarketRegimeUpdate(message as MarketRegimeUpdateMessage);
              break;
            case 'monitoring_update':
              handleMonitoringUpdate(message as MonitoringUpdateMessage);
              break;
            case 'trade_update':
              handleTradeUpdate(message as TradeUpdateMessage);
              break;
            case 'bot_metrics_update':
              handleBotMetricsUpdate(message as BotMetricsUpdateMessage);
              break;
            case 'infrastructure_update':
              handleInfrastructureUpdate(message as unknown as InfrastructureUpdateMessage);
              break;
            case 'spread_update':
              handleIndicatorUpdate(message as SpreadUpdateMessage);
              break;
            case 'momentum_update':
              handleIndicatorUpdate(message as MomentumUpdateMessage);
              break;
            case 'velocity_update':
              handleIndicatorUpdate(message as VelocityUpdateMessage);
              break;
            case 'fragility_update':
              handleIndicatorUpdate(message as FragilityUpdateMessage);
              break;
            case 'logs_update':
              handleLogsUpdate(message as LogsUpdateMessage);
              break;
            default:
              console.log('Unknown message type:', message.type);
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err);
      setError('Failed to create connection');
    }
  };

  const disconnect = () => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setConnected(false);
  };

  const reconnect = () => {
    disconnect();
    // Wait a bit before reconnecting
    reconnectTimeoutRef.current = setTimeout(() => {
      connect();
    }, 2000);
  };

  const sendMessage = (message: any) => {
    if (socketRef.current && connected && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    }
  };

  const subscribeToNews = () => {
    sendMessage({
      type: 'subscribe',
      subscription_type: 'news'
    });
  };

  const subscribeToMarketRegime = () => {
    sendMessage({
      type: 'subscribe',
      subscription_type: 'market_regime'
    });
  };

  const subscribeToMonitoring = () => {
    sendMessage({
      type: 'subscribe',
      subscription_type: 'monitoring'
    });
  };

  const subscribeToTrades = () => {
    sendMessage({
      type: 'subscribe',
      subscription_type: 'trades'
    });
  };

  const subscribeToBotMetrics = () => {
    sendMessage({
      type: 'subscribe',
      subscription_type: 'bot_metrics'
    });
  };

  const subscribeToInfrastructure = () => {
    sendMessage({
      type: 'subscribe',
      subscription_type: 'infrastructure'
    });
  };

  const subscribeToSpread = () => {
    sendMessage({
      type: 'subscribe',
      subscription_type: 'spread'
    });
  };

  const subscribeToMomentum = () => {
    sendMessage({
      type: 'subscribe',
      subscription_type: 'momentum'
    });
  };

  const subscribeToVelocity = () => {
    sendMessage({
      type: 'subscribe',
      subscription_type: 'velocity'
    });
  };

  const subscribeToFragility = () => {
    sendMessage({
      type: 'subscribe',
      subscription_type: 'fragility'
    });
  };

  const subscribeToLogs = () => {
    sendMessage({
      type: 'subscribe',
      subscription_type: 'logs'
    });
  };

  // Handle specific message types
  const handleNewsUpdate = (message: NewsUpdateMessage) => {
    // You can add specific logic here, like showing notifications
    if (message.data.importance === 'critical') {
      // Show critical news notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Critical News Alert', {
          body: message.data.title,
          icon: '/favicon.ico'
        });
      }
    }
  };

  const handleMarketRegimeUpdate = (message: MarketRegimeUpdateMessage) => {
    // Handle market regime updates
    console.log('Market regime updated:', message.data);
  };

  const handleMonitoringUpdate = (message: MonitoringUpdateMessage) => {
    // Handle monitoring updates
    console.log('Monitoring updated:', message.data);
  };

  const handleTradeUpdate = (message: TradeUpdateMessage) => {
    // Handle trade updates and invalidate relevant queries
    console.log('Trade updated:', message.data.action, message.data);

    // Invalidate trades queries to trigger refetch
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.trades.ongoing });
    queryClient.invalidateQueries({ queryKey: ['trades'] });

    // Invalidate account-specific queries if we have the account_id
    if (message.data.account_id) {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.account(message.data.account_id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accountDetails(message.data.account_id) });
    }
  };

  const handleBotMetricsUpdate = (message: BotMetricsUpdateMessage) => {
    const { category, bot_name } = message.data;
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.observability.metrics });
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.observability.metricsByCategory(category)
    });
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.observability.metricsByBot(category, bot_name)
    });
  };

  const handleInfrastructureUpdate = (message: InfrastructureUpdateMessage) => {
    const metrics = message.data;
    setInfrastructureServers(prev => {
      const updated = new Map(prev);
      updated.set(metrics.hostname, metrics);
      return updated;
    });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.infrastructure.metrics });
  };

  const handleLogsUpdate = (_message: LogsUpdateMessage) => {
    queryClient.invalidateQueries({ queryKey: ['logs'] });
  };

  const handleIndicatorUpdate = (_message: SpreadUpdateMessage | MomentumUpdateMessage | VelocityUpdateMessage | FragilityUpdateMessage) => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.indicators.latest });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.indicators.summary });
  };

  // Setup ping interval to keep connection alive
  useEffect(() => {
    if (connected && socketRef.current) {
      const pingInterval = setInterval(() => {
        sendMessage({ type: 'ping' });
      }, 30000); // Ping every 30 seconds

      return () => clearInterval(pingInterval);
    }
  }, [connected]);

  // Connect on mount
  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, []);

  const value: WebSocketContextType = {
    connected,
    lastMessage,
    error,
    infrastructureServers,
    subscribeToNews,
    subscribeToMarketRegime,
    subscribeToMonitoring,
    subscribeToTrades,
    subscribeToBotMetrics,
    subscribeToInfrastructure,
    subscribeToSpread,
    subscribeToMomentum,
    subscribeToVelocity,
    subscribeToFragility,
    subscribeToLogs,
    reconnect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
